/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useEditorStore } from "@/stores/editor";
import { $getRoot } from "lexical";

const PUNCTUATION = [
  ",",
  "，",
  ".",
  "。",
  ":",
  "：",
  ";",
  "；",
  "[",
  "]",
  "【",
  "]",
  "】",
  "{",
  "｛",
  "}",
  "｝",
  "(",
  "（",
  ")",
  "）",
  "<",
  "《",
  ">",
  "》",
  "$",
  "￥",
  "!",
  "！",
  "?",
  "？",
  "~",
  "～",
  "'",
  "’",
  '"',
  "“",
  "”",
  "*",
  "/",
  "\\",
  "&",
  "%",
  "@",
  "#",
  "^",
  "、",
  "、",
  "、",
  "、",
];

// Got from https://github.com/byn9826/words-count/blob/master/src/globalWordsCount.js
export function getWordCountOfTextNode(text: string): number {
  if (text.length === 0 || text.trim().length === 0) return 0;

  // Change punctuation to empty space
  const punctuationRegex = new RegExp("\\" + PUNCTUATION.join(" "), "g");
  text = text.replace(punctuationRegex, " ");

  // Remove all kind of symbols
  text = text.replace(/[\uFF00-\uFFEF\u2000-\u206F]/g, "");

  // Format white space character
  text = text.replace(/\s+/, " ");

  // Split text by white space (For European languages)
  let words = text.split(" ");

  words = words.filter((word: string) => word.trim());

  // Match latin, cyrillic, Malayalam letters and numbers
  const common =
    "(\\d+)|[a-zA-Z\u00C0-\u00FF\u0100-\u017F\u0180-\u024F\u0250-\u02AF\u1E00-\u1EFF\u0400-\u04FF\u0500-\u052F\u0D00-\u0D7F]+|";

  // Match Chinese Hànzì, the Japanese Kanji and the Korean Hanja
  const cjk =
    "\u2E80-\u2EFF\u2F00-\u2FDF\u3000-\u303F\u31C0-\u31EF\u3200-\u32FF\u3300-\u33FF\u3400-\u3FFF\u4000-\u4DBF\u4E00-\u4FFF\u5000-\u5FFF\u6000-\u6FFF\u7000-\u7FFF\u8000-\u8FFF\u9000-\u9FFF\uF900-\uFAFF";

  // Match Japanese Hiragana, Katakana, Rōmaji
  const jp = "\u3040-\u309F\u30A0-\u30FF\u31F0-\u31FF\u3190-\u319F";

  // Match Korean Hangul
  const kr =
    "\u1100-\u11FF\u3130-\u318F\uA960-\uA97F\uAC00-\uAFFF\uB000-\uBFFF\uC000-\uCFFF\uD000-\uD7AF\uD7B0-\uD7FF";

  // eslint-disable-next-line no-misleading-character-class
  const regex = new RegExp(common + "[" + cjk + jp + kr + "]", "g");
  let wordCountOfTextNode = 0;
  words.forEach((word: string) => {
    // Stores the count of matached charecters so we can treat them as seperate words (as per the grammer of these langaues)
    let singleCharecterWordsCount = 0;
    let matched;
    do {
      matched = regex.exec(word);
      if (matched) singleCharecterWordsCount++;
    } while (matched);
    if (singleCharecterWordsCount === 0) {
      wordCountOfTextNode++;
    } else {
      wordCountOfTextNode += singleCharecterWordsCount;
    }
  });

  return wordCountOfTextNode;
}

export const getWordCount = () => {
  const { setEditor } = useEditorStore();

  const root = $getRoot();
  const text = root.getTextContent();
  const numWords = getWordCountOfTextNode(text);
  setEditor((editorState) => {
    editorState.wordCount = numWords;
  });
};

export const WordCountPlugin = () => {
  const { editor } = useEditorStore();
  const numWords = editor.wordCount;

  const formattedWordCount =
    numWords >= 99999
      ? `${(numWords / 1000).toFixed(1)}K`
      : numWords.toString();

  return (
    <div className="absolute bottom-4 right-4 flex justify-center print:hidden w-[80px]">
      <p className="text-2xs text-zinc-400 dark:text-zinc-600 leading-none flex-shrink truncate flex-wrap">
        {formattedWordCount}
      </p>
      <p className="text-2xs text-zinc-400 dark:text-zinc-600 leading-none flex-shrink flex-wrap ml-1">
        words
      </p>
    </div>
  );
};

export default WordCountPlugin;
